import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient, { apiGet, apiPost, apiDelete } from '../../shared/utils/apiConfig';

// all products - محسن للأداء
export const fetchProducts = createAsyncThunk("products/fetchProducts", async (page = 1, { rejectWithValue }) => {
  try {
    const data = await apiGet(`/stores/all_products?page=${page}`);
    return data;
  } catch (error) {
    // معالجة أنواع الأخطاء المختلفة
    let message = "فشل في تحميل البيانات";
    
    if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
      message = "خطأ في الاتصال بالإنترنت. تحقق من اتصالك بالشبكة";
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      message = "انتهت مهلة الاتصال. تحقق من اتصالك بالإنترنت";
    } else if (error.response?.status === 0) {
      message = "لا يمكن الوصول للخادم. تحقق من اتصالك بالإنترنت";
    } else if (error.response?.data?.result?.message) {
      message = error.response.data.result.message;
    } else if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    
    return rejectWithValue({ message });
  }
});
// get units - محسن للأداء
export const fetchUnits = createAsyncThunk("units/fetchUnits", async () => {
  try {
    const data = await apiGet('/get_units');
    return data.result;
  } catch (error) {
    const message = error.response?.data?.result?.message || "فشل في تحميل الوحدات";
    throw new Error(message);
  }
});
// get store profile - محسن للأداء
export const fetchStoreProfile = createAsyncThunk("storeProfile/fetchStoreProfile", async () => {
  try {
    const data = await apiGet('/stores/store_profile');
    return data.result;
  } catch (error) {
    const message = error.response?.data?.result?.message || "فشل في تحميل بيانات المتجر";
    throw new Error(message);
  }
});

// add products - محسن للأداء
export const addProducts = createAsyncThunk(
  "products/add",
  async (userData, { rejectWithValue }) => {
    try {
      // دعم إرسال FormData للصور والوحدات
      const response = await apiClient.post('/products/store_new_product', userData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      // التعامل مع أخطاء الـ validation
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        // عرض أول خطأ
        const firstError = Object.values(errors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          return rejectWithValue(firstError[0]);
        }
      }
      return rejectWithValue(error.response?.data?.message || "فشل في الإضافة");
    }
  }
);
// get products record - محسن للأداء
export const productsRecord = createAsyncThunk(
  "products/productsRecord",
  async (id, { rejectWithValue }) => {
    try {
      const data = await apiGet(`/products/store_product_details/${id}`);
      return data.result;
    } catch (error) {
      const message = error.response?.data?.message || "فشل في تحميل تفاصيل المنتج";
      return rejectWithValue({ message });
    }
  }
);
// update products - محسن للأداء
export const updateProducts = createAsyncThunk(
  "products/updateProducts",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/products/update_product/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const data = response.data;
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "فشل في التحديث");
    }
  }
);
// delete product - محسن للأداء
export const deleteProducts = createAsyncThunk(
  "products/deleteProducts",
  async (id, { rejectWithValue }) => {
    try {
      const data = await apiDelete(`/products/delete_product/${id}`);
      return data;
    } catch (error) {
      // معالجة أنواع الأخطاء المختلفة
      let message = "فشل في حذف المنتج";
      
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        message = "خطأ في الاتصال بالإنترنت. تحقق من اتصالك بالشبكة";
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        message = "انتهت مهلة الاتصال. تحقق من اتصالك بالإنترنت";
      } else if (error.response?.status === 0) {
        message = "لا يمكن الوصول للخادم. تحقق من اتصالك بالإنترنت";
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.message) {
        message = error.message;
      }
      
      return rejectWithValue({ message });
    }
  }
);
const ProductsSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    units: [],
    storeProfile: {},
    record: null,
    meta: null,
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.meta = action.payload.meta;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.isLoading = false;
        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
        state.error = isOffline
          ? 'لا يوجد اتصال بالإنترنت. تحقق من الشبكة وحاول مرة أخرى'
          : (action.payload?.message || action.error?.message || 'فشل في تحميل البيانات');
      })
      .addCase(fetchUnits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.meta = action.payload.meta;
        state.units = action.payload;
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      .addCase(fetchStoreProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.meta = action.payload.meta;
        state.storeProfile = action.payload;
      })
      .addCase(fetchStoreProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      .addCase(addProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(productsRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(productsRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload;
      })
      .addCase(productsRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      })
      .addCase(updateProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to update record";
      })
      .addCase(deleteProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteProducts.fulfilled, (state, action) => {
        state.isLoading = false;

        // Remove from data array inside products
        if (state.products?.data) {
          state.products.data = state.products.data.filter(
            (record) => record.id !== action.meta.arg
          );
        }

        state.success = action.payload?.message || "Deleted successfully";
      })
      .addCase(deleteProducts.rejected, (state, action) => {
        state.isLoading = false;
        // لو مفيش نت بنحط رسالة واضحة
        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
        state.error = isOffline
          ? 'لا يوجد اتصال بالإنترنت. تحقق من الشبكة وحاول مرة أخرى'
          : (action.payload?.message || action.payload || action.error?.message || 'Failed to delete products');
      });
  },
});

export const { clearState } = ProductsSlice.actions;
export default ProductsSlice.reducer;
